'use client';

import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface RevenueAnalyticsProps {
  timeRange: string;
  detailed?: boolean;
}

const mockRevenueData = {
  totalRevenue: 487650,
  averageOrderValue: 391,
  revenueGrowth: 18.5,
  topCustomerRevenue: 45200,
  monthlyRevenue: [
    { month: 'Jan', revenue: 98500, orders: 245 },
    { month: 'Feb', revenue: 112300, orders: 287 },
    { month: 'Mar', revenue: 125800, orders: 321 },
    { month: 'Apr', revenue: 151050, orders: 394 },
  ],
  revenueByService: [
    { name: 'Air Freight', value: 185000, percentage: 38 },
    { name: 'Ocean Freight', value: 146300, percentage: 30 },
    { name: 'Ground Transport', value: 97530, percentage: 20 },
    { name: 'Warehousing', value: 39060, percentage: 8 },
    { name: 'Customs', value: 19530, percentage: 4 },
  ],
  topCustomers: [
    { name: 'TechCorp Inc.', revenue: 45200, orders: 87 },
    { name: 'Global Traders', revenue: 38900, orders: 102 },
    { name: 'Logistics Pro', revenue: 32100, orders: 76 },
    { name: 'ShipFast Ltd.', revenue: 28700, orders: 64 },
    { name: 'Express Cargo', revenue: 24500, orders: 58 },
  ],
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

export default function RevenueAnalytics({ timeRange, detailed = false }: RevenueAnalyticsProps) {
  return (
    <div className="space-y-6">
      {/* Key Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CurrencyDollarIcon" size={24} className="text-green-600" />
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">+{mockRevenueData.revenueGrowth}%</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Revenue</h3>
          <p className="text-3xl font-bold text-foreground">${mockRevenueData.totalRevenue.toLocaleString()}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ShoppingCartIcon" size={24} className="text-primary" />
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">+8.2%</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Avg Order Value</h3>
          <p className="text-3xl font-bold text-foreground">${mockRevenueData.averageOrderValue}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUpIcon" size={24} className="text-green-600" />
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Growth</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Revenue Growth</h3>
          <p className="text-3xl font-bold text-foreground">{mockRevenueData.revenueGrowth}%</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="UserIcon" size={24} className="text-secondary" />
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">Top</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Top Customer</h3>
          <p className="text-3xl font-bold text-foreground">${mockRevenueData.topCustomerRevenue.toLocaleString()}</p>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Monthly Revenue Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mockRevenueData.monthlyRevenue}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              formatter={(value: number) => `$${value.toLocaleString()}`}
            />
            <Legend />
            <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue by Service */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Revenue by Service</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={mockRevenueData.revenueByService}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name} ${percentage}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {mockRevenueData.revenueByService.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Customers */}
        {detailed && (
          <div className="bg-card rounded-xl p-6 shadow-card border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Top Customers by Revenue</h3>
            <div className="space-y-3">
              {mockRevenueData.topCustomers.map((customer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">{customer.name}</p>
                    <p className="text-xs text-muted-foreground">{customer.orders} orders</p>
                  </div>
                  <p className="text-lg font-bold text-foreground">${customer.revenue.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}